import { featureFlags } from "@/config/feature-flag";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import {
  changeRecipientStatus,
  createRecipient,
  cycleSummary,
  decideVoucher,
  findVoucher,
  listRecipients,
  listVouchers,
  removeRecipient,
  updateRecipient,
  type RecipientListQuery,
  type RecipientPayload,
  type VoucherDecisionPayload,
} from "./rh-mock-store";

interface MockRequest {
  method: string;
  path: string;
  search: URLSearchParams;
  body: unknown;
}

interface MockRoute {
  method: string;
  pattern: RegExp;
  handle: (request: MockRequest, params: string[]) => MockOutcome;
}

interface MockError {
  status: number;
  data: { messages: string[] };
}

type MockOutcome = { data: unknown } | { error: MockError };

const HTTP_NOT_FOUND: number = 404;
const HTTP_CONFLICT: number = 409;

const MOCK_LATENCY_MS: number = 250;

const DEFAULT_PAGE: number = 1;
const DEFAULT_PER_PAGE: number = 10;

const VERSION_CONFLICT_MESSAGE: string =
  "Este vale já foi decidido por outro operador do RH.";

const NOT_FOUND_MESSAGE: string = "Registro não encontrado.";

const ACTIVE_STATUS: string = "ACTIVE";

const INACTIVE_STATUS: string = "INACTIVE";

interface RecipientUpdatePayload extends Partial<RecipientPayload> {
  status?: string;
  version: number;
}

const toServedRecipient = (recipient: {
  id: number;
  sector: string;
  name: string;
  email: string;
  shipping_types: string[];
  active: boolean;
}): Record<string, unknown> => ({
  id: recipient.id,
  sector: recipient.sector,
  name: recipient.name,
  email: recipient.email,
  shipping_types: recipient.shipping_types,
  status: recipient.active ? ACTIVE_STATUS : INACTIVE_STATUS,
  version: 1,
});

const notFound = (): MockOutcome => ({
  error: { status: HTTP_NOT_FOUND, data: { messages: [NOT_FOUND_MESSAGE] } },
});

const readList = (search: URLSearchParams, key: string): string[] => {
  const raw: string | null = search.get(key);
  if (!raw) return [];
  return raw.split(",");
};

const toRecipientQuery = (search: URLSearchParams): RecipientListQuery => ({
  search: search.get("search") ?? "",
  sectors: readList(search, "sectors"),
  shipping_types: readList(search, "shipping_types"),
  active: readList(search, "active").map(
    (value: string): boolean => value === "true"
  ),
  page: Number(search.get("page") ?? DEFAULT_PAGE),
  perPage: Number(search.get("perPage") ?? DEFAULT_PER_PAGE),
});

const ROUTES: readonly MockRoute[] = [
  {
    method: "GET",
    pattern: /^\/shipping-recipient$/,
    handle: (request: MockRequest): MockOutcome => {
      const page = listRecipients(toRecipientQuery(request.search));

      return {
        data: { data: page.data.map(toServedRecipient), meta: page.meta },
      };
    },
  },
  {
    method: "POST",
    pattern: /^\/shipping-recipient$/,
    handle: (request: MockRequest): MockOutcome => ({
      data: createRecipient(request.body as RecipientPayload),
    }),
  },
  {
    method: "PUT",
    pattern: /^\/shipping-recipient\/(\d+)$/,
    handle: (request: MockRequest, params: string[]): MockOutcome => {
      const payload = request.body as RecipientUpdatePayload;
      const id: number = Number(params[0]);

      const updated = payload.status
        ? changeRecipientStatus(id, payload.status === ACTIVE_STATUS)
        : updateRecipient(id, payload as RecipientPayload);

      return updated ? { data: updated } : notFound();
    },
  },
  {
    method: "DELETE",
    pattern: /^\/shipping-recipient\/(\d+)$/,
    handle: (_request: MockRequest, params: string[]): MockOutcome =>
      removeRecipient(Number(params[0])) ? { data: null } : notFound(),
  },
  {
    method: "GET",
    pattern: /^\/payroll-voucher$/,
    handle: (request: MockRequest): MockOutcome => ({
      data: listVouchers(request.search.get("status")),
    }),
  },
  {
    method: "GET",
    pattern: /^\/payroll-voucher\/cycle\/summary$/,
    handle: (): MockOutcome => ({ data: cycleSummary() }),
  },
  {
    method: "PUT",
    pattern: /^\/payroll-voucher\/(\d+)\/decision$/,
    handle: (request: MockRequest, params: string[]): MockOutcome => {
      const id: number = Number(params[0]);
      const payload = request.body as VoucherDecisionPayload;
      const current = findVoucher(id);

      if (!current) return notFound();

      if (current.version !== payload.version) {
        return {
          error: {
            status: HTTP_CONFLICT,
            data: { messages: [VERSION_CONFLICT_MESSAGE] },
          },
        };
      }

      const decided = decideVoucher(id, payload);
      return decided ? { data: decided } : notFound();
    },
  },
];

const toMockRequest = (args: unknown): MockRequest => {
  const asString: boolean = typeof args === "string";
  const raw = args as { url?: string; method?: string; body?: unknown };
  const url: string = asString ? (args as string) : raw.url ?? "";
  const [path, query] = url.split("?");

  return {
    method: (asString ? "GET" : raw.method ?? "GET").toUpperCase(),
    path,
    search: new URLSearchParams(query ?? ""),
    body: asString ? undefined : raw.body,
  };
};

const findRoute = (request: MockRequest): MockRoute | undefined =>
  ROUTES.find(
    (route: MockRoute): boolean =>
      route.method === request.method && route.pattern.test(request.path)
  );

const delay = (ms: number): Promise<void> =>
  new Promise((resolve): void => {
    setTimeout(resolve, ms);
  });

export const withRhMocks =
  (baseQuery: BaseQueryFn): BaseQueryFn =>
  async (args, api, extraOptions) => {
    if (!featureFlags.RH_USE_MOCKS) return baseQuery(args, api, extraOptions);

    const request: MockRequest = toMockRequest(args);
    const route: MockRoute | undefined = findRoute(request);

    if (!route) return baseQuery(args, api, extraOptions);

    const params: string[] = (
      request.path.match(route.pattern) ?? []
    ).slice(1);

    await delay(MOCK_LATENCY_MS);
    return route.handle(request, params);
  };
